/**
 * Compute-host extractors.
 *
 * Project rows from `PLANT_COMPUTE_INDEX_CYPHER` and
 * `PLANT_COMPUTE_DETAIL_CYPHER` into the typed
 * `PlantComputeHostSummary` / `PlantComputeHostDetail` shapes the
 * Plant Viewer's Compute tab renders against.
 *
 * Pure functions, no RTK / no Neo4j driver — trivially unit-testable.
 */

import { extractNode, toIntCount } from '../graph-row'
import { compareByNameThenId } from '../sort'
import { extractComputeRefs, extractControllerRefs } from '../refs'
import type {
  CypherReadResult,
  PlantComputeHost,
  PlantComputeHostDetail,
  PlantComputeHostSummary,
  PlantDatabase,
  PlantMonitoredAsset,
  PlantTagProvider
} from '../types'

/**
 * Coalesce the Compute node's gateway URL across the field-name drift
 * between the legacy `:Gateway` label (which stored the live-data URL
 * as `gateway_url`) and the new `:Compute` spec (which calls it `url`).
 * Real data carries one, the other, or both depending on ingest path;
 * normalizing here means downstream callers can rely on `host.url`
 * regardless of source.
 */
export function normalizeComputeHost(host: PlantComputeHost): PlantComputeHost {
  if (host.url) return host
  const legacy = (host as unknown as Record<string, unknown>)['gateway_url']
  if (typeof legacy === 'string' && legacy.length > 0) {
    return { ...host, url: legacy }
  }
  return host
}

/**
 * Cypher `collect(distinct {provider, tagCount})` from
 * `getPlantComputeDetail`. Filter sentinel rows (provider missing) and
 * project each into a `PlantTagProvider` with its `tagCount` baked in.
 */
export function extractTagProviderSummaries(value: unknown): PlantTagProvider[] {
  if (!Array.isArray(value)) return []
  const out: PlantTagProvider[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const provider = extractNode<PlantTagProvider>(obj['provider'])
    if (!provider) continue
    out.push({
      ...provider,
      tagCount: toIntCount(obj['tagCount'])
    })
  }
  out.sort(compareByNameThenId)
  return out
}

/**
 * Cypher `collect(distinct {database, tableCount})` from
 * `getPlantComputeDetail`. Same sentinel-filter pattern as
 * {@link extractTagProviderSummaries}.
 */
export function extractDatabaseSummaries(value: unknown): PlantDatabase[] {
  if (!Array.isArray(value)) return []
  const out: PlantDatabase[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const database = extractNode<PlantDatabase>(obj['database'])
    if (!database) continue
    out.push({
      ...database,
      tableCount: toIntCount(obj['tableCount'])
    })
  }
  out.sort(compareByNameThenId)
  return out
}

/**
 * Project the `monitoredAssets` projection (list of `{id, name, kind,
 * type}` maps) into typed {@link PlantMonitoredAsset}s, dropping rows
 * without an `id` and name-sorting for stable rendering.
 */
export function extractMonitoredAssets(value: unknown): PlantMonitoredAsset[] {
  if (!Array.isArray(value)) return []
  const out: PlantMonitoredAsset[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const id = typeof obj['id'] === 'string' ? obj['id'] : null
    if (!id) continue
    const name = typeof obj['name'] === 'string' ? obj['name'] : id
    const kind = obj['kind'] === 'line' ? 'line' : 'equipment'
    const type = typeof obj['type'] === 'string' ? obj['type'] : undefined
    out.push({ id, name, kind, ...(type ? { type } : {}) })
  }
  out.sort(compareByNameThenId)
  return out
}

/**
 * Project a standalone `:Database` (no incoming `HAS_DATABASE`) into a
 * `db_server` Compute host so the Compute tab can render dump catalogs
 * beside JDBC database servers.
 */
export function standaloneDatabaseAsComputeHost(
  database: PlantDatabase | null
): PlantComputeHost | null {
  if (!database) return null
  return {
    id: database.id,
    _id: database._id,
    name: database.name,
    compute_type: 'db_server',
    ...(database.url ? { url: database.url } : {})
  }
}

/**
 * True when a catalog is a DuckDB dump from graph identity
 * (`database_type` / `duckdb://`), independent of registry posture.
 */
export function isDuckDbPlantDatabase(
  database: Pick<PlantDatabase, 'database_type' | 'url'>
): boolean {
  const dbType = (database.database_type ?? '').toUpperCase().trim()
  if (dbType === 'DUCKDB') return true
  const url = (database.url ?? '').toLowerCase()
  return url.startsWith('duckdb:')
}

/**
 * True when a catalog is a proxy-managed read-only mirror from graph
 * identity (`database_type` `PSS` / `managed://`), independent of registry
 * posture. Today the only managed kind is a Hexagon PlantState Suite mirror.
 */
export function isManagedPlantDatabase(
  database: Pick<PlantDatabase, 'database_type' | 'url'>
): boolean {
  const dbType = (database.database_type ?? '').toUpperCase().trim()
  if (dbType === 'PSS') return true
  const url = (database.url ?? '').toLowerCase()
  return url.startsWith('managed:')
}

/**
 * True when the Plant DB query engine can open this catalog. Ignition
 * hosts go through WebDev; live SQL catalogs (postgres / SQL Server /
 * MySQL) need a URL; DuckDB dumps and managed mirrors are queryable from
 * graph identity (`database_type` / `duckdb://` / `managed://`) without a
 * Compute parent.
 */
export function isQueryablePlantDatabase(
  database: Pick<PlantDatabase, 'database_type' | 'url'>,
  hostComputeType?: string
): boolean {
  const typeKey = (hostComputeType ?? '').toLowerCase().replace(/[\s_-]/g, '')
  if (typeKey === 'ignition') return true
  if (isDuckDbPlantDatabase(database)) return true
  if (isManagedPlantDatabase(database)) return true
  if (!database.url) return false
  const dbType = (database.database_type ?? '').toUpperCase().trim()
  if (dbType.startsWith('POSTGRES')) return true
  if (dbType === 'MYSQL' || dbType === 'MARIADB') return true
  if (dbType === 'MSSQL' || dbType === 'SQLSERVER' || dbType === 'SQL SERVER') return true
  return false
}

/**
 * Project one row from `PLANT_COMPUTE_INDEX_CYPHER` (or the standalone
 * Database index) into a typed {@link PlantComputeHostSummary}. Returns
 * `null` for the sentinel row where the Compute is missing
 * (`OPTIONAL MATCH` mismatch).
 */
export function mapPlantComputeIndexRow(
  row: Record<string, unknown>
): PlantComputeHostSummary | null {
  const host = extractNode<PlantComputeHost>(row['c'])
  if (!host) return null
  return {
    ...normalizeComputeHost(host),
    tagProviderCount: toIntCount(row['tagProviderCount']),
    databaseCount: toIntCount(row['databaseCount']),
    controllers: extractControllerRefs(row['controllers']),
    dcsControllers: extractControllerRefs(row['dcsControllers']),
    gateways: extractComputeRefs(row['gateways'])
  }
}

/**
 * Project the single row returned by `PLANT_COMPUTE_DETAIL_CYPHER` into
 * the full {@link PlantComputeHostDetail} bundle. Falls back to a
 * synthetic "Unknown compute host" stub when the Compute id wasn't
 * found in the graph (page renders with an empty list rather than
 * crashing). A standalone dump Database is projected as a `db_server`
 * host via {@link standaloneDatabaseAsComputeHost}.
 */
export function mapPlantComputeDetail(
  raw: CypherReadResult,
  computeId: string
): PlantComputeHostDetail {
  const row = raw.results[0] ?? {}
  const host = normalizeComputeHost(
    extractNode<PlantComputeHost>(row['c']) ??
      standaloneDatabaseAsComputeHost(extractNode<PlantDatabase>(row['standaloneDatabase'])) ?? {
        id: computeId,
        _id: computeId,
        name: 'Unknown compute host'
      }
  )
  return {
    host,
    isStray: row['isStray'] === true,
    tagProviders: extractTagProviderSummaries(row['tagProviderSummaries']),
    databases: extractDatabaseSummaries(row['databaseSummaries']),
    controllers: extractControllerRefs(row['controllers']),
    dcsControllers: extractControllerRefs(row['dcsControllers']),
    gateways: extractComputeRefs(row['gateways']),
    monitoredAssets: extractMonitoredAssets(row['monitoredAssets'])
  }
}
